import { isRecord } from '@/shared/utils/apiResponse';
import { esperar } from '@/shared/utils/esperar';
import { proximoIntervaloComBackoff } from '@/shared/utils/proximoIntervaloComBackoff';
import { apiGet, apiPost, request } from '@/shared/utils/requestBuilder';

import type {
  CreateFlowBuilderSessionInput,
  FlowBuilderCancelResult,
  FlowBuilderMessagePage,
  FlowBuilderMessagesQuery,
  FlowBuilderSessionDto,
  FlowBuilderSessionPage,
  FlowBuilderSessionsQuery,
  FlowBuilderTurnStatusDto,
  FlowBuilderTurnStreamInput,
  FlowBuilderTurnStreamOutcome,
} from '../../types/flowBuilder/flowBuilderApi';
import type { FlowBuilderStreamCursor as StreamCursor } from '../../types/flowBuilder/flowBuilderStore';
import type { FlowValidationProblem } from '../../types/negotiationAgents';
import {
  isFlowBuilderTerminalEvent,
  parseFlowBuilderStreamEvent,
} from '../../utils/flowBuilder/flowBuilderStreamEvent';
import { parseSseEventBlock, splitSseBuffer } from '../../utils/flowBuilder/sseEventBlock';
import { readAgentResponse } from '../../utils/negotiationAgents/apiResponse';

/**
 * A API corta a request de stream em 300 s e o turno pode durar mais (prazo duro de 480 s no
 * worker, mais o tempo na fila): reconectar é o caminho feliz, não a exceção. O teto conta
 * reconexões; quem desiste de uma rede caída é a sequência de tentativas sem progresso.
 */
const MAX_RECONNECTIONS = 12;
const MAX_ATTEMPTS_WITHOUT_PROGRESS = 4;
const BACKOFF_START_MS = 1_000;
const BACKOFF_MAX_MS = 8_000;
/**
 * O core manda `: keep-alive` a cada 15 s de silêncio. Três faltas seguidas = conexão meio-aberta
 * (proxy ou Wi-Fi caíram sem FIN): o `read()` nunca resolveria, então a leitura é cortada e o laço
 * reconecta pelo `Last-Event-ID`.
 */
const STREAM_IDLE_TIMEOUT_MS = 45_000;

const SSE_HEADERS = { Accept: 'text/event-stream' };

const builderEndpoint = (agentId: string) => `/admin/negotiationAgents/${agentId}/builder`;

const streamProblem = (code: string, message: string): FlowValidationProblem => ({
  code,
  message,
});

const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'AbortError';

const isNotFoundProblem = (error: unknown): boolean => isRecord(error) && error.status === 404;

/** `fetch` rejeita com `TypeError` quando a request nem chega a ter resposta. */
const isNetworkFailure = (error: unknown): boolean => error instanceof TypeError;

async function streamBodyOf(response: Response): Promise<ReadableStream<Uint8Array>> {
  if (!response.ok) await readAgentResponse<never>(response);
  if (!response.body) {
    throw streamProblem('STREAM_UNAVAILABLE', 'O navegador não abriu o stream do FlowBuilder.');
  }
  return response.body;
}

function postTurn(input: FlowBuilderTurnStreamInput): Promise<Response> {
  return request({
    endpoint: `${builderEndpoint(input.agentId)}/turns`,
    method: 'POST',
    body: input.request,
    headers: { ...SSE_HEADERS, 'Idempotency-Key': input.turnId },
    signal: input.signal,
  });
}

function getTurnStream(input: FlowBuilderTurnStreamInput, lastEventId: string | null) {
  return request({
    endpoint: `${builderEndpoint(input.agentId)}/turns/${input.turnId}/stream`,
    method: 'GET',
    headers: lastEventId ? { ...SSE_HEADERS, 'Last-Event-ID': lastEventId } : SSE_HEADERS,
    signal: input.signal,
  });
}

/**
 * Repetir o POST só é seguro porque a `Idempotency-Key` é o `turnId`: se a primeira tentativa
 * chegou ao servidor, a segunda devolve o stream do mesmo turno em vez de abrir outro. Recusa do
 * servidor (409, 422, 429, 503) não se repete — sobe como problema.
 */
async function openTurnWithRetry(input: FlowBuilderTurnStreamInput) {
  let backoffMs = BACKOFF_START_MS;
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await streamBodyOf(await postTurn(input));
    } catch (error) {
      if (!isNetworkFailure(error)) throw error;
      if (attempt >= MAX_ATTEMPTS_WITHOUT_PROGRESS) {
        throw streamProblem(
          'NETWORK_ERROR',
          'Não foi possível falar com o FlowBuilder. Verifique a conexão e tente de novo.',
        );
      }
    }
    await esperar(backoffMs, input.signal);
    backoffMs = proximoIntervaloComBackoff(backoffMs, 2, BACKOFF_MAX_MS);
  }
}

function handleSseBlock(rawBlock: string, cursor: StreamCursor, input: FlowBuilderTurnStreamInput) {
  const block = parseSseEventBlock(rawBlock);
  if (!block) return;
  // O cursor anda mesmo com JSON ilegível: reconectar antes dele reentregaria o mesmo bloco.
  if (block.eventId) cursor.lastEventId = block.eventId;
  const event = parseFlowBuilderStreamEvent(block.data);
  if (!event) return;
  if (isFlowBuilderTerminalEvent(event)) cursor.terminalEvent = event.type;
  input.onEvent(event, block.eventId);
}

/** `cancel()` também fecha a conexão por baixo do stream, em vez de deixá-la pendurada. */
async function readBeforeIdleTimeout(reader: ReadableStreamDefaultReader<Uint8Array>) {
  let idleTimer: ReturnType<typeof setTimeout> | undefined;
  const idle = new Promise<never>((_, reject) => {
    idleTimer = setTimeout(() => {
      reader.cancel().catch(() => undefined);
      reject(streamProblem('STREAM_IDLE', 'O stream do FlowBuilder parou de responder.'));
    }, STREAM_IDLE_TIMEOUT_MS);
  });
  try {
    return await Promise.race([reader.read(), idle]);
  } finally {
    clearTimeout(idleTimer);
  }
}

async function readStream(
  body: ReadableStream<Uint8Array>,
  cursor: StreamCursor,
  input: FlowBuilderTurnStreamInput,
) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!cursor.terminalEvent) {
      const { done, value } = await readBeforeIdleTimeout(reader);
      if (done) break;
      const { blocks, rest } = splitSseBuffer(buffer + decoder.decode(value, { stream: true }));
      blocks.forEach((block) => handleSseBlock(block, cursor, input));
      buffer = rest;
    }
    // O que sobra no buffer quando o stream fecha é bloco sem a linha em branco final, cortado no
    // meio (o core fecha todo bloco com `\n\n`). Ele é descartado, como manda o SSE: tratá-lo
    // avançaria o `Last-Event-ID` para um evento que não chegou inteiro, e a reconexão o pularia.
  } finally {
    reader.releaseLock();
  }
}

/** Queda no meio da leitura não é erro do turno: quem decide se reconecta é o laço. */
async function readUntilDrop(
  body: ReadableStream<Uint8Array>,
  cursor: StreamCursor,
  input: FlowBuilderTurnStreamInput,
) {
  try {
    await readStream(body, cursor, input);
  } catch (error) {
    if (isAbortError(error)) throw error;
  }
}

/**
 * 404 encerra na hora: o turno não existe ou não é deste usuário, e reconectar não muda isso. As
 * demais falhas devolvem `null` e contam como tentativa sem progresso.
 */
async function reopenStream(input: FlowBuilderTurnStreamInput, cursor: StreamCursor) {
  try {
    const response = await getTurnStream(input, cursor.lastEventId);
    if (response.status === 404) await readAgentResponse<never>(response);
    return response.ok && response.body ? response.body : null;
  } catch (error) {
    if (isAbortError(error) || isNotFoundProblem(error)) throw error;
    return null;
  }
}

/** Progresso = o cursor andou ou o turno terminou; stream que abre e fecha vazio não conta. */
async function resumeOnce(input: FlowBuilderTurnStreamInput, cursor: StreamCursor) {
  const body = await reopenStream(input, cursor);
  if (!body) return false;
  const before = cursor.lastEventId;
  await readUntilDrop(body, cursor, input);
  return cursor.terminalEvent !== null || cursor.lastEventId !== before;
}

async function resumeUntilTerminal(input: FlowBuilderTurnStreamInput, cursor: StreamCursor) {
  let reconnections = 0;
  let attemptsWithoutProgress = 0;
  let backoffMs = BACKOFF_START_MS;

  while (
    !cursor.terminalEvent &&
    reconnections < MAX_RECONNECTIONS &&
    attemptsWithoutProgress < MAX_ATTEMPTS_WITHOUT_PROGRESS
  ) {
    reconnections += 1;
    if (await resumeOnce(input, cursor)) {
      attemptsWithoutProgress = 0;
      backoffMs = BACKOFF_START_MS;
      continue;
    }
    attemptsWithoutProgress += 1;
    await esperar(backoffMs, input.signal);
    backoffMs = proximoIntervaloComBackoff(backoffMs, 2, BACKOFF_MAX_MS);
  }
}

export const flowBuilderService = {
  async listSessions({ agentId, page, limit }: FlowBuilderSessionsQuery) {
    const response = await apiGet(`${builderEndpoint(agentId)}/sessions`, { page, limit });
    return readAgentResponse<FlowBuilderSessionPage>(response);
  },

  async createSession({ agentId, title, testEscritorioId }: CreateFlowBuilderSessionInput) {
    const response = await apiPost(`${builderEndpoint(agentId)}/sessions`, {
      ...(title ? { title } : {}),
      ...(testEscritorioId ? { testEscritorioId } : {}),
    });
    return readAgentResponse<FlowBuilderSessionDto>(response);
  },

  async listMessages({ agentId, sessionId, beforeSeq, limit }: FlowBuilderMessagesQuery) {
    const response = await apiGet(`${builderEndpoint(agentId)}/sessions/${sessionId}/messages`, {
      limit,
      ...(beforeSeq !== undefined ? { beforeSeq } : {}),
    });
    return readAgentResponse<FlowBuilderMessagePage>(response);
  },

  async cancelTurn(agentId: string, turnId: string) {
    const response = await apiPost(`${builderEndpoint(agentId)}/turns/${turnId}/cancel`);
    return readAgentResponse<FlowBuilderCancelResult>(response);
  },

  async getTurnStatus(agentId: string, turnId: string) {
    const response = await apiGet(`${builderEndpoint(agentId)}/turns/${turnId}`);
    return readAgentResponse<FlowBuilderTurnStatusDto>(response);
  },

  /**
   * Abre o turno (`request` presente) ou só reconecta a um turno vivo, entrega cada evento a
   * `onEvent` e reconecta por `GET /turns/:turnId/stream` com `Last-Event-ID` até `done`/`error`.
   */
  async streamTurn(input: FlowBuilderTurnStreamInput): Promise<FlowBuilderTurnStreamOutcome> {
    const cursor: StreamCursor = { lastEventId: null, terminalEvent: null };
    if (input.request) await readUntilDrop(await openTurnWithRetry(input), cursor, input);
    await resumeUntilTerminal(input, cursor);
    if (!cursor.terminalEvent) {
      throw streamProblem(
        'STREAM_LOST',
        'A conexão com o FlowBuilder caiu e não voltou. O turno pode seguir no servidor: reabra a conversa para acompanhar.',
      );
    }
    return { terminalEvent: cursor.terminalEvent, lastEventId: cursor.lastEventId };
  },
};
